import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

export type BaseFieldControlValue = string | boolean;

export function resolveBaseFieldValidationResult(result: unknown) {
  if (result === null) return null;
  if (typeof result === 'string' && result.trim().length > 0) return result;
  throw new TypeError(
    'BaseField validate must return a non-empty error string or null.',
  );
}

interface BaseFieldContextValue {
  invalid: boolean;
  registerControl: (controlKey: symbol) => () => void;
  notifyBlur: (value: BaseFieldControlValue) => void;
  notifyChange: (value: BaseFieldControlValue) => void;
}

export const BaseFieldContext = createContext<BaseFieldContextValue | null>(
  null,
);

export function useBaseFieldControl() {
  const context = useContext(BaseFieldContext);
  const controlKeyRef = useRef<symbol | null>(null);
  if (controlKeyRef.current === null) {
    controlKeyRef.current = Symbol('BaseFieldControl');
  }

  const registerControl = context?.registerControl;
  useEffect(() => {
    if (!registerControl || controlKeyRef.current === null) return undefined;
    return registerControl(controlKeyRef.current);
  }, [registerControl]);

  const notifyBlur = context?.notifyBlur;
  const notifyChange = context?.notifyChange;

  return {
    invalid: context?.invalid ?? false,
    notifyBlur: useCallback(
      (value: BaseFieldControlValue) => notifyBlur?.(value),
      [notifyBlur],
    ),
    notifyChange: useCallback(
      (value: BaseFieldControlValue) => notifyChange?.(value),
      [notifyChange],
    ),
  };
}
